<script setup lang="ts">
import MarkdownIt from 'markdown-it';

const markdown = `
# **Términos y Condiciones de Servicio**

## **Engeni: Cleepo y Flokee**

## **Introducción**

El presente documento establece los términos y condiciones contractuales (en adelante, los "Términos y Condiciones") que regulan la relación entre ENGENI INTERNATIONAL LLC y sus subsidiarias ENGENI ARGENTINA SRL y ENGENI MEXICO S DE RL DE CV (según corresponda al territorio, conjuntamente denominadas "ENGENI", "nosotros", "nuestro/a") y usted (en adelante, el "Cliente", "Usuario" o "usted"), en relación con el acceso y uso de los servicios denominados Flokee y/o Cleepo (en adelante, el "Servicio" o los "Servicios").

Para los clientes en Argentina, el proveedor del servicio será ENGENI ARGENTINA SRL. Para los clientes en México, el proveedor del servicio será ENGENI MEXICO S DE RL DE CV. Para clientes en el resto del mundo, el proveedor del servicio será ENGENI INTERNATIONAL LLC.

Al acceder, registrarse o utilizar cualquiera de nuestros Servicios, usted acepta estar legalmente vinculado por estos Términos y Condiciones. Si no está de acuerdo con alguna parte de estos términos, no debe utilizar nuestros Servicios.

## **1. Definiciones**

A los efectos de estos Términos y Condiciones, los siguientes términos tendrán los significados que se indican a continuación:

1.1. **Flokee**: Plataforma de software desarrollada por ENGENI que resuelve el tráfico de e-commerce, conectando con Google para generar visitas calificadas al sitio web del Cliente.

1.2. **Cleepo**: Solución basada en Inteligencia Artificial (IA) desarrollada por ENGENI, diseñada específicamente para PyMEs, negocios y profesionales independientes, que combina IA avanzada, procesos automatizados y estrategias digitales personalizadas para conectar a los Clientes con clientes potenciales.

1.3. **Cliente**: Persona física o jurídica que contrata cualquiera de los Servicios de ENGENI.

1.4. **Panel de Control**: Interfaz digital a través de la cual el Cliente puede monitorear resultados, ver estadísticas y, dependiendo del servicio contratado, acceder a la base de datos de contactos generados.

1.5. **Clientes Potenciales**: Consultas o contactos de personas interesadas en los servicios/productos del Cliente, como resultado del tráfico generado por la publicidad gestionada por ENGENI.

1.6. **Planes de Servicio**: Diferentes niveles de servicios ofrecidos para cada solución (Flokee o Cleepo), cada uno con características específicas según se detalla en el sitio web correspondiente.

1.7. **Periodo de Aprendizaje**: Periodo inicial (entre 45 y 60 días corridos para Flokee, y de 30 días para Cleepo) durante el cual el rendimiento de las campañas puede no alcanzar la media del mercado, ya que los algoritmos están en fase de optimización.

## **2. Objeto del servicio**

2.1. El objeto del presente contrato es la prestación de los Servicios Flokee y/o Cleepo, según el servicio contratado por el Cliente, que incluye:

### **Para Flokee:**

a) Automatización y optimización de publicidad digital en Google Ads para e-commerce. b) Conexión con las plataformas de Google Analytics, Google Merchant, Google Ads y Google Tag Manager. c) Generación de tráfico calificado al e-commerce del Cliente. d) Acceso a un Panel de Control con estadísticas relevantes.

### **Para Cleepo:**

a) Creación de un Resumen de Negocio mediante IA basado en la información proporcionada por el Cliente. b) Desarrollo de una presencia web profesional con página web optimizada para dispositivos móviles. c) Obtención de dominio ".com" exclusivo para el negocio del Cliente (según el plan contratado). d) Creación y gestión automatizada de campañas publicitarias en Google Ads. e) Generación de consultas de personas interesadas a través de los canales seleccionados por el Cliente. f) Acceso a un Panel de Control para monitoreo de resultados en tiempo real. g) Asistente virtual de IA para la gestión de consultas (en planes que lo incluyan).

2.2. ENGENI se reserva el derecho de modificar, actualizar o mejorar los Servicios, notificando al Cliente cuando dichos cambios afecten sustancialmente la prestación de los mismos.

## **3. Registro y activación**

3.1. Para utilizar los Servicios, el Cliente debe: a) Registrarse en el sitio web correspondiente al servicio contratado proporcionando datos reales de contacto. b) Completar la información requerida sobre su negocio. c) Seleccionar el plan que mejor se adapte a sus necesidades. d) Configurar el medio de pago para el primer cargo y los cobros mensuales automáticos.

3.2. El Cliente declara y garantiza que toda la información proporcionada durante el registro es verdadera, precisa, actual y completa.

3.3. La activación del Servicio se realizará en un plazo de 24-96 horas hábiles para Cleepo y de hasta 10 días hábiles para Flokee, una vez completados todos los pasos del registro y realizado el primer pago. El Cliente recibirá una notificación por correo electrónico confirmando la activación.

3.4. Si ENGENI necesita material adicional (imágenes o información), el equipo de soporte contactará al Cliente. Si no se recibe este material en un plazo de 3 días hábiles, ENGENI utilizará imágenes genéricas relacionadas con el sector del Cliente o información predeterminada.

## **4. Responsabilidades del cliente**

4.1. El Cliente se compromete a: a) Responder rápidamente a las consultas recibidas a través de los Servicios. b) Trabajar en la conversión de los contactos generados en ventas. c) Mantener la calidad prometida de sus productos o servicios. d) Informar a ENGENI sobre cambios en sus productos, servicios o zonas de cobertura. e) Mantener actualizado su medio de pago y asegurar fondos suficientes para el débito mensual automático. f) Utilizar los Servicios de conformidad con la legislación aplicable y estos Términos y Condiciones.

4.2. El Cliente reconoce y acepta que la conversión de consultas o tráfico en ventas efectivas es responsabilidad exclusiva del Cliente y no de ENGENI.

4.3. El Cliente es responsable de mantener la confidencialidad de sus credenciales de acceso al Panel de Control y notificar inmediatamente a ENGENI sobre cualquier uso no autorizado de su cuenta.

## **5. Responsabilidades de Engeni**

5.1. ENGENI se compromete a: a) Optimizar constantemente las campañas mediante IA de acuerdo con el plan contratado. b) Administrar eficientemente la inversión publicitaria del Cliente. c) Proporcionar informes claros y útiles a través del Panel de Control. d) Mantener los sistemas actualizados y seguros. e) Implementar mejoras basadas en la retroalimentación. f) Proporcionar soporte técnico dentro de los horarios establecidos.

5.2. ENGENI no garantiza: a) Posiciones específicas en los resultados de búsqueda de Google. b) Un número específico de visitas, consultas o clientes potenciales. c) La conversión de tráfico o consultas en ventas. d) Resultados inmediatos, dado que los algoritmos necesitan tiempo para optimizarse (Periodo de Aprendizaje). e) Control sobre factores macroeconómicos que pueden afectar el rendimiento del negocio del Cliente.

## **6. Propiedad intelectual y licencias**

6.1. ENGENI es el único titular de todos los derechos de propiedad intelectual sobre los Servicios Flokee y Cleepo, incluyendo pero no limitado a su software, algoritmos, diseños, marcas, logotipos, campañas publicitarias, estrategias de segmentación, copias, diseños y contenidos generados.

6.2. ENGENI otorga al Cliente una licencia limitada, no exclusiva, no transferible y revocable para utilizar los Servicios de acuerdo con estos Términos y Condiciones durante la vigencia del contrato.

6.3. El Cliente otorga a ENGENI una licencia no exclusiva para utilizar, reproducir, adaptar y publicar los materiales e información proporcionados (incluyendo logotipos, marcas y contenidos) con el único propósito de prestar los Servicios.

6.4. A la terminación del contrato, el Cliente no tendrá derecho a seguir utilizando el dominio, la página web o cualquier otro activo digital creado por ENGENI como parte de los Servicios, ni tendrá derecho a reclamar propiedad, uso o modificación de las campañas publicitarias fuera del uso ordinario de los Servicios.

6.5. En el caso específico de Flokee, la totalidad de las campañas publicitarias, estrategias de segmentación, copias y diseños generados a través del Servicio son propiedad exclusiva de ENGENI.

## **7. Instalación de etiquetas de seguimiento**

7.1. El Cliente autoriza expresamente a ENGENI a instalar y operar etiquetas de seguimiento en su sitio web o e-commerce para recopilar datos de audiencia y optimizar la publicidad.

7.2. ENGENI utilizará dichos datos exclusivamente para la optimización de las campañas del Cliente y en cumplimiento de las leyes aplicables de privacidad.

## **8. Uso y compartición de datos**

8.1. Al aceptar estos Términos y Condiciones, el Cliente otorga a ENGENI el derecho de utilizar los datos e información generados a través del uso de los Servicios para los siguientes fines: a) Proporcionar, mantener, mejorar y desarrollar los Servicios y sus características. b) Desarrollar y entrenar los algoritmos de inteligencia artificial que sustentan los Servicios. c) Generar análisis estadísticos, informes de rendimiento e investigaciones de mercado. d) Crear datos agregados y anonimizados que puedan ser utilizados para mejorar los Servicios o desarrollar nuevos productos.

8.2. ENGENI podrá compartir datos anonimizados y agregados con terceros, incluidos Google y Meta, para mejorar la optimización de campañas.

8.3. El Cliente garantiza que cuenta con todos los derechos y permisos necesarios para compartir con ENGENI cualquier información o datos personales proporcionados durante el uso de los Servicios.

8.4. ENGENI implementa medidas técnicas y organizativas apropiadas para proteger los datos personales contra el acceso, uso o divulgación no autorizados.

8.5. El Cliente reconoce y acepta expresamente que ENGENI puede compartir datos con terceros en las siguientes circunstancias: a) Con proveedores de servicios que ayudan a operar los Servicios (como proveedores de hosting, procesadores de pago, etc.). b) Cuando sea necesario para cumplir con obligaciones legales o requerimientos de autoridades competentes. c) En caso de reorganización empresarial, fusión, venta o transferencia de negocio. d) Datos agregados y anonimizados con fines estadísticos, de análisis o mejora de los Servicios.

8.6. ENGENI no venderá los datos personales identificables del Cliente a terceros sin su consentimiento expreso.

8.7. ENGENI cumplirá con todas las leyes aplicables de protección de datos, incluyendo, pero sin limitarse a, la Ley de Protección de Datos Personales de Argentina (Ley 25.326), la Ley Federal de Protección de Datos Personales en Posesión de los Particulares de México, y el Reglamento General de Protección de Datos (GDPR) para clientes en territorios donde sea aplicable.

## **9. Acceso a datos del cliente**

9.1. El Cliente concede a ENGENI acceso a su catálogo de productos, información de transacciones y otros datos relevantes para la optimización del rendimiento de los Servicios.

9.2. En el caso específico de Flokee, el Cliente autoriza el acceso y la gestión de sus cuentas de Google Analytics, Google Merchant y la creación de nuevas cuentas si fuera necesario.

## **10. Pago y facturación**

10.1. Los Servicios de ENGENI son de suscripción mensual con renovación automática.

10.2. El Cliente se compromete a pagar la tarifa correspondiente al plan seleccionado, que se cobrará automáticamente a través del método de pago registrado.

10.3. Los medios de pago aceptados incluyen:

* Tarjetas de crédito: VISA, MasterCard, American Express, Naranja, Nativa.  
* Tarjetas de débito: VISA, MasterCard, Maestro.  
* Otros medios que ENGENI pueda implementar y comunicar oportunamente.

10.4. El Cliente autoriza a ENGENI a realizar cargos recurrentes a su método de pago registrado. La falta de pago resultará en la suspensión de los Servicios.

10.5. ENGENI se reserva el derecho de modificar las tarifas de los Planes de Servicio, notificando al Cliente con al menos 30 días de anticipación.

10.6. Los impuestos aplicables serán adicionales a las tarifas publicadas, según corresponda en cada jurisdicción.

10.7. Para Flokee, se aclara específicamente que los planes incluyen la inversión en medios publicitarios (Google Ads) en la proporción indicada para cada plan, y la comisión por el servicio de ENGENI.

## **11. Plazo y terminación**

11.1. El contrato tiene una vigencia mínima obligatoria de 2 meses, período necesario para que los algoritmos aprendan y se optimicen.

11.2. Transcurrido el período mínimo, el contrato se renovará automáticamente por períodos mensuales sucesivos.

11.3. El Cliente puede solicitar la cancelación en cualquier momento desde su Panel de Control, pero la baja se hará efectiva al cumplirse los 2 meses mínimos o al finalizar el ciclo de facturación en curso si ya se ha superado dicho período.

11.4. ENGENI podrá terminar el contrato en cualquier momento, sin responsabilidad alguna, si: a) El Cliente incumple estos Términos y Condiciones. b) El Cliente no realiza los pagos correspondientes. c) El Cliente utiliza los Servicios de manera fraudulenta o ilegal. d) ENGENI decide discontinuar los Servicios.

11.5. Cualquiera de las partes podrá resolver el contrato en caso de incumplimiento grave de la otra parte, mediante notificación escrita, sin perjuicio de las acciones legales que correspondan.

11.6. En el caso específico de Flokee, al solicitar la baja: a) Si las cuentas de Google (Analytics, Merchant, Ads) fueron creadas por ENGENI, éstas no se eliminarán, sino que quedarán en "pausa". Si el Cliente decide volver a usar Flokee en unos meses, la reactivación podría ser más rápida. Sin embargo, si pasan más de 6 meses sin actividad, Google podría eliminarlas automáticamente. b) Si las cuentas ya eran del Cliente (Analytics y/o Merchant), éstas permanecerán sin cambios, ya que ENGENI solo solicita permisos para gestionarlas, pero no tiene capacidad para borrarlas o modificarlas. c) El Cliente conservará el acceso a la plataforma en su versión básica, lo que le permitirá ver sus datos y cuentas configuradas, pero las funcionalidades avanzadas estarán deshabilitadas.

## **12. Derecho de admisión y suspensión de servicios**

12.1. **Derecho de suspensión y terminación**

ENGENI se reserva el derecho exclusivo e irrevocable de suspender temporalmente o terminar definitivamente la prestación de los Servicios, así como deshabilitar o eliminar cualquier contenido, página web, campaña publicitaria o cuenta asociada, en cualquier momento y sin previo aviso, cuando a su exclusivo criterio considere que:

a) El negocio, actividad comercial o contenido del Cliente pueda estar asociado con actividades que contravengan la legislación aplicable o las buenas prácticas comerciales;

b) El uso de los Servicios pueda violar las políticas, términos de servicio o directrices de terceros (incluyendo, pero no limitándose a Google, Meta, TikTok y otras plataformas digitales);

c) La actividad del Cliente pueda comprometer la reputación, integridad o intereses comerciales de ENGENI;

d) Se detecten patrones de comportamiento que sugieran prácticas comerciales cuestionables o potencialmente fraudulentas;

e) El Cliente incumpla de manera sustancial cualquiera de las obligaciones establecidas en estos Términos y Condiciones;

f) La continuidad de los Servicios pueda exponer a ENGENI a riesgos legales, regulatorios o reputacionales.

12.2. **Proceso de suspensión**

En caso de suspensión temporal, ENGENI notificará al Cliente las razones de la medida y, cuando sea posible y apropiado, otorgará un plazo razonable para que el Cliente pueda rectificar la situación o proporcionar las aclaraciones necesarias. Si el Cliente no subsana las causas de la suspensión dentro del plazo establecido, ENGENI podrá proceder a la terminación definitiva del contrato.

12.3. **Terminación inmediata**

En casos donde ENGENI determine que existe un riesgo inminente o cuando la naturaleza de la infracción así lo amerite, la terminación podrá ser inmediata y definitiva, sin período de subsanación.

12.4. **Notificación**

ENGENI notificará al Cliente sobre la suspensión o terminación a través del correo electrónico registrado o mediante los canales de comunicación disponibles en el Panel de Control, dentro de un plazo razonable posterior a la implementación de la medida.

12.5. **Efectos de la terminación por esta causa**

En caso de terminación anticipada del contrato por las causas establecidas en esta cláusula:

a) ENGENI procederá inmediatamente a la desactivación de todas las campañas publicitarias, páginas web y servicios asociados;

b) El Cliente perderá todo derecho de acceso al Panel de Control y a los servicios contratados;

c) Si la terminación ocurre antes de la finalización del ciclo de facturación vigente, ENGENI podrá, a su exclusivo criterio, proceder a un reembolso parcial del monto abonado, descontando los gastos administrativos correspondientes a la terminación adelantada y los costos de servicios ya prestados.

12.6. **Sin limitación de otros derechos**

El ejercicio de los derechos establecidos en esta cláusula no limitará otros derechos o recursos que ENGENI pueda tener bajo estos Términos y Condiciones o bajo la legislación aplicable.

12.7. **Exención de responsabilidad**

El Cliente acepta expresamente que ENGENI no será responsable por ningún daño, pérdida de beneficios, interrupción del negocio o cualquier otro perjuicio que pueda derivarse de la suspensión o terminación de los Servicios bajo esta cláusula.

## **13. Limitación de responsabilidad**

13.1. ENGENI proporciona los Servicios "tal cual" y "según disponibilidad", sin garantías de ningún tipo, ya sean expresas o implícitas.

13.2. ENGENI no será responsable por daños indirectos, incidentales, especiales, consecuenciales o punitivos, ni por pérdida de beneficios, ingresos, datos, oportunidades de negocio o fondo de comercio.

13.3. La responsabilidad total de ENGENI derivada de o relacionada con este contrato no excederá el monto total pagado por el Cliente a ENGENI durante los 3 meses anteriores al evento que dio lugar a la responsabilidad.

13.4. ENGENI no será responsable por: a) El contenido proporcionado por el Cliente. b) La conversión de tráfico o consultas en ventas. c) La calidad de los productos o servicios ofrecidos por el Cliente. d) Problemas técnicos ajenos a su control. e) Factores macroeconómicos o cambios en el mercado que puedan afectar al negocio del Cliente.

## **14. Soporte técnico**

14.1. ENGENI proporcionará soporte técnico en los siguientes horarios:

* Lunes a Viernes: 9:00 a 18:00 (GMT-3 Argentina)  
* Tiempo de respuesta: 24 horas hábiles (urgencias en el día)  
* No hay atención en fines de semana y feriados

14.2. Los canales de soporte disponibles son:

* WhatsApp (atención a todo el Mundo): (+54) 11 5263 3802  
* Argentina: (+54) 11 5263 3802  
* México: (+52) 55 4000 3807  
* Estados Unidos y resto del Mundo: (+1) 786 600 0612

## **15. Confidencialidad**

15.1. Cada parte se compromete a mantener la confidencialidad de toda la información confidencial recibida de la otra parte en relación con los Servicios.

15.2. Se considera información confidencial toda información identificada como tal o que por su naturaleza deba ser tratada como confidencial.

15.3. Las obligaciones de confidencialidad sobrevivirán a la terminación del contrato por un período de 2 años.

## **16. Fuerza mayor**

16.1. Ninguna de las partes será responsable por incumplimientos o retrasos en el cumplimiento de sus obligaciones debido a causas de fuerza mayor, incluyendo pero no limitado a desastres naturales, pandemias, actos de guerra o terrorismo, disturbios civiles, fallas en los servicios de telecomunicaciones o energía eléctrica, o cualquier otra causa fuera del control razonable de las partes.

## **17. Modificaciones de los términos y condiciones**

17.1. ENGENI se reserva el derecho de modificar estos Términos y Condiciones en cualquier momento, notificando al Cliente a través del correo electrónico registrado o mediante un aviso en la plataforma.

17.2. Si el Cliente continúa utilizando los Servicios después de la fecha de entrada en vigor de las modificaciones, se considerará que ha aceptado los nuevos términos.

17.3. Si el Cliente no está de acuerdo con los términos modificados, deberá dejar de utilizar los Servicios y podrá terminarlos de acuerdo con la cláusula 11.

## **18. Legislación aplicable y resolución de disputas**

18.1. Para clientes en Argentina, estos Términos y Condiciones se regirán e interpretarán de acuerdo con las leyes de la República Argentina.

18.2. Para clientes en México, estos Términos y Condiciones se regirán e interpretarán de acuerdo con las leyes de los Estados Unidos Mexicanos.

18.3. Para clientes en el resto del mundo, estos Términos y Condiciones se regirán e interpretarán de acuerdo con las leyes del Estado de Florida, Estados Unidos, sin tener en cuenta los principios de conflicto de leyes.

18.4. Cualquier disputa, controversia o reclamo que surja de o esté relacionado con estos Términos y Condiciones, incluida la validez, invalidez, incumplimiento o terminación de los mismos, se resolverá mediante arbitraje.

18.5. Para clientes en Argentina, el arbitraje se llevará a cabo de acuerdo con el Reglamento de Arbitraje del Centro Empresarial de Mediación y Arbitraje (CEMA) en Buenos Aires.

18.6. Para clientes en México, el arbitraje se llevará a cabo de acuerdo con el Reglamento de Arbitraje del Centro de Arbitraje de México (CAM) en Ciudad de México.

18.7. Para clientes en el resto del mundo, el arbitraje se llevará a cabo de acuerdo con el Reglamento de Arbitraje Comercial de la Asociación Americana de Arbitraje vigente en la fecha en que se inicie dicho arbitraje, en Miami, Florida, Estados Unidos.

18.8. El número de árbitros será uno, el idioma del arbitraje será el español o inglés (según la preferencia del Cliente).

18.9. El laudo arbitral será final y vinculante para ambas partes, y su ejecución podrá presentarse ante cualquier tribunal que tenga jurisdicción sobre la parte contra la cual se busca la ejecución.

18.10. Sin perjuicio de lo anterior, ENGENI se reserva el derecho de iniciar acciones legales ante los tribunales competentes para la protección de sus derechos de propiedad intelectual, la obtención de medidas cautelares o cualquier otro remedio legal urgente.

## **19. Disposiciones generales**

19.1. **Divisibilidad**: Si alguna disposición de estos Términos y Condiciones se considera inválida o inaplicable, las demás disposiciones seguirán siendo válidas y aplicables.

19.2. **Cesión**: El Cliente no podrá ceder o transferir este contrato ni ninguno de sus derechos u obligaciones sin el consentimiento previo por escrito de ENGENI. ENGENI podrá ceder este contrato libremente.

19.3. **Notificaciones**: Todas las notificaciones relacionadas con estos Términos y Condiciones deberán enviarse por correo electrónico a las direcciones proporcionadas durante el registro o a través del Panel de Control.

19.4. **Renuncia**: La falta de ejercicio o el retraso en el ejercicio de cualquier derecho o recurso no constituirá una renuncia a dicho derecho o recurso.

19.5. **Acuerdo completo**: Estos Términos y Condiciones, junto con cualquier otro documento referenciado expresamente, constituyen el acuerdo completo entre las partes con respecto a los Servicios y reemplazan cualquier acuerdo o entendimiento previo.

## **20. Contacto**

Para cualquier consulta relacionada con estos Términos y Condiciones o los Servicios de ENGENI, puede contactarnos a través de:

* WhatsApp (atención a todo el Mundo): (+54) 11 5263 3802  
* Argentina: (+54) 11 5263 3802  
* México: (+52) 55 4000 3807  
* Estados Unidos y resto del Mundo: (+1) 786 600 0612

**Fecha de última actualización**: 13 de agosto de 2025

Al hacer clic en "Acepto" o utilizar los Servicios, usted reconoce haber leído, comprendido y aceptado estos Términos y Condiciones.

---

© 2025 Engeni International LLC. Todos los derechos reservados.
`;

const md = new MarkdownIt();

const terms = computed(() => {
  return md.render(markdown);
});
</script>

<template>
  <div class="min-h-screen py-8 lg:px-8">
    <div class="mx-auto max-w-4xl rounded-lg bg-white md:p-8">
      <article
        class="prose max-w-none prose-headings:font-medium prose-p:text-semantic-text-secondary prose-strong:font-medium"
        v-safe-html="terms"
      />
    </div>
  </div>
</template>
